import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Typography, IconButton } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { Box } from '@mui/material';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Selection, Page, VirtualScroll } from '@syncfusion/ej2-react-grids';
import { useGetRulesByContractIdQuery } from '../../../../services/ContractService';
import { useAppDispatch } from '../../../../app/hooks';
import { toggleRuleModal } from '../../../../services/ContractService/contractSlice';
import { BaseRuleView } from '../../../../services/ContractService/types/contractTypes';

const RuleList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const contractId = searchParams.get('id');
  const dispatch = useAppDispatch();
  
  const { data: rules = [], isLoading, error } = useGetRulesByContractIdQuery(
    { contractId: contractId! },
    { skip: !contractId }
  );

  const handleViewRule = (ruleId: string) => {
    dispatch(toggleRuleModal(ruleId));
  };

  const actionTemplate = (props: BaseRuleView) => {
    return (
      <IconButton
        size="small"
        onClick={() => handleViewRule(props.id)}
        title="View/Edit Rule"
      >
        <Visibility />
      </IconButton>
    );
  };

  const gridSettings = {
    allowPaging: false, // Disable paging when using virtual scrolling
    allowSelection: true,
    selectionSettings: { mode: 'Row' as any },
    enableVirtualization: true,
    enableColumnVirtualization: false, // Only enable row virtualization
    pageSettings: { pageSize: 50 } // Virtual page size for better performance
  };

  if (isLoading) {
    return <Typography>Loading rules...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error">
        Error loading rules: {error && 'data' in error ? JSON.stringify(error.data) : 'Unknown error'}
      </Typography>
    );
  }

  return (
    <Box>
      {rules.length === 0 ? (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ p: 2 }}>
          No rules found for this contract.
        </Typography>
      ) : (
        <GridComponent
          dataSource={rules}
          allowPaging={gridSettings.allowPaging}
          pageSettings={gridSettings.pageSettings}
          allowSelection={gridSettings.allowSelection}
          selectionSettings={gridSettings.selectionSettings}
          enableVirtualization={gridSettings.enableVirtualization}
          enableColumnVirtualization={gridSettings.enableColumnVirtualization}
          height="400"
        >
          <ColumnsDirective>
            <ColumnDirective field="ruleType" headerText="Type" width="150" />
            <ColumnDirective field="description" headerText="Description" width="300" />
            <ColumnDirective
              headerText="Actions"
              width="100"
              textAlign="Center"
              template={actionTemplate}
              allowSorting={false}
            />
          </ColumnsDirective>
          <Inject services={[Selection, VirtualScroll]} />
        </GridComponent>
      )}
    </Box>
  );
};

export { RuleList };